/* AppBanner component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, State, Method, Prop, Event, EventEmitter, h } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars

import { AbstractComponent } from '@cewe-designsystem/abstract';
import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection';
import UtilTradingPartner from '@cewe-designsystem/util_tradingpartner';
import AppBannerTemplate from './cwc-app-banner.template';
import { UtilLocalStorage } from '@cewe-designsystem/util_storage';

const DEFAULT_CLOSED_STORAGE_KEY = 'MPSPhotoWorldApp';

/*
 * @slot headline - Showing the first line of text
 * @slot subline - Showing the second line of text
 * @slot appStoreImg - The given Apple App Store Icon
 * @slot playStoreImg - The given Google PlayStore Icon
 * @slot closingIcon - HTML element used as caption for the button to close the infobar e.g. an img or svg
 */

@Component({
  tag: 'cwc-component-app-banner',
  styleUrl: './cwc-app-banner.scss',
  shadow: true,
})
export class AppBannerComponent {
  /**
   * properties
   */
  @Prop({ reflect: true }) opened: boolean = true;
  @Prop({ reflect: false }) linkAppStore: string = '#Link2AppStore';
  @Prop({ reflect: false }) linkPlayStore: string = '#Link2PlayStore';
  @Prop({ reflect: false }) btnLabel: string = 'VIEW';
  /**
   * @deprecated use closedStorageKey instead.
   */
  @Prop({ reflect: false }) cookieName: string;
  /**
   * name of the key to persistence the closing state of the app banner in the browser storage.
   */
  @Prop({ reflect: false }) closedStorageKey: string = DEFAULT_CLOSED_STORAGE_KEY;
  @Prop({ reflect: false }) productId: number;
  @Prop({ reflect: false }) project: string = '';
  @Prop({ reflect: false }) keyAccountId: number;
  @Prop({ reflect: false }) language: string = 'de';
  @Prop({ reflect: false }) country: string = 'DE';
  @Prop({ reflect: false }) dataPath: string = '';

  readonly operatingSystem: string;
  readonly supportedOperatingSystems = ['ios', 'android'];

  readonly defaultClosedTimeMS: number = 1209600000; // 14 days

  /**
   * If the referenced product is available in the MPS order channel
   */
  @State()
  isProductAvailableInMPS: boolean = false;

  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * Event thrown if the app-banner is clicked
   *
   * @docs
   * @event on:app-banner:view
   */
  @Event({
    eventName: 'on:app-banner:view',
  })
  onAppBannerView: EventEmitter;

  /**
   * Event thrown if the app-banner is closed
   *
   * @docs
   * @event on:app-banner:close
   */
  @Event({
    eventName: 'on:app-banner:close',
  })
  onAppBannerClose: EventEmitter;

  /**
   * Click the app-banner to close
   *
   * @function close
   * @docs
   */
  @Method()
  async close() {
    this.onClose();
  }

  /**
   * @constructor
   */
  constructor() {
    const utilBrowserDetection = new UtilBrowserDetection();
    this.operatingSystem = utilBrowserDetection.getOs();

    if (this.isInProductContext) {
      this.loadProductAvailability();
    }
  }

  onClose() {
    /* set a cookie to avoid another opening */
    UtilLocalStorage.set(this.closedStorageKey, 'true', this.defaultClosedTimeMS);
    /* close the banner */
    this.opened = false;
    /* emit the event - for tracking and things */
    this.onAppBannerClose.emit(this.element);
  }

  /**
   * Click the app-banner to view
   *
   * @function view
   * @docs
   */
  @Method()
  async view() {
    this.onView();
  }

  onView() {
    /* set a cookie to avoid another opening */
    UtilLocalStorage.set(this.closedStorageKey, 'true', this.defaultClosedTimeMS);
    /* close the banner */
    this.opened = false;
    /* emit the event - for tracking and things */
    this.onAppBannerView.emit(this.element);
  }

  getStoreLink() {
    if (this.isSupportedOS && this.operatingSystem == 'android') {
      return this.linkPlayStore;
    } else if (this.isSupportedOS && this.operatingSystem == 'ios') {
      return this.linkAppStore;
    } else {
      return '#unsupported';
    }
  }

  /**
   * Check if the app banner is enabled in the current environment (website)
   *
   * @private
   * @return {boolean}
   */
  isEnabled(): boolean {
    // Check if it is an Android or iOS device
    if (!this.isSupportedOS) {
      return false;
    }

    // Hide the app banner if the cookie is set
    if (UtilLocalStorage.isSet(this.closedStorageKey)) {
      return false;
    }

    // Hide the app banner if the product is not available for the MPS order channel
    if (this.isInProductContext && !this.isProductAvailableInMPS) {
      return false;
    }

    return true;
  }

  get isSupportedOS(): boolean {
    return this.supportedOperatingSystems.includes(this.operatingSystem);
  }

  /**
   * If the app banner is in a product context if it has product information.
   * E.g. if it is placed on a product information page (PIP)
   */
  get isInProductContext(): boolean {
    const isSet = (value) => !!value;

    return isSet(this.productId) && isSet(this.keyAccountId) && isSet(this.language) && isSet(this.dataPath);
  }

  componentWillRender() {
    // set old cookiename as new storage key if it is set
    if (this.cookieName?.length > 0) {
      this.closedStorageKey = this.cookieName;
    }
  }

  loadProductAvailability() {
    const utilTradingPartner = new UtilTradingPartner(this.dataPath, this.keyAccountId);
    (async () => {
      const productOrderChannels = await utilTradingPartner.getProductOrderChannels(
        this.productId,
        this.keyAccountId,
        this.language,
        this.country,
        this.project,
      );
      this.isProductAvailableInMPS = productOrderChannels && productOrderChannels['mobileOrder'];
    })();
  }

  /**
   * render
   */
  render() {
    return <AppBannerTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(AppBannerComponent.prototype, AbstractComponent.prototype);
export interface AppBannerComponent extends AbstractComponent {}

/**
* cwc-component-app-banner
* @example ComponentAppBanner
* @docs
*

 <cwc-component-app-banner opened
   link-app-store="https://app-store.lnk"
   link-play-store="https://play-store.lnk"
   btn-label="Open"
   cookie-name="FotoFunApp"
 >

   <span slot="headline">The Headline</span>
   <span slot="subline">A Very Cute Subline</span>
   <img slot="appStoreImg" src="./fotowelt_iOS_1024.png" src-set="" />
   <img slot="playStoreImg" src="./fotowelt_Android_512.png" src-set="" />

 </cwc-component-app-banner>
 */
